import React, { useEffect, useState, useRef } from "react"
import Typography from "@mui/material/Typography"
import { Box, ButtonBase, Dialog, DialogActions, IconButton } from "@mui/material";
import { useMenuStore } from "../store/menuState";
import { useOrderStore } from "../store/order";
import { useInfoStore } from "../store/userInfo";
import { createOrder } from "../services/order.service";
import moment from "moment";
import { isBrowser } from "react-device-detect";
import useIdleTimeout from '../context/IdleTimeout';
import { logger } from '../utils/logger';


const ConfirmPopup = () => {
  const { tableInfo, userInfo } = useInfoStore()
  const { idleState, categories, setIdleState, setCategories, setTimerInfo, deleteTimerInfo } = useMenuStore()
  const { cart, totalCount, totalPrice, categoryInCart, setSnackMode, setShowSnackbar, setOpenCart, setCompleteList, resetOrderCart, setOpenComplete } = useOrderStore();
  const storeId = userInfo?.storeId ? Number(userInfo.storeId) : 0;
  const [time, setTime] = useState(20)
  const [processing, setProcessing] = useState(false)
  const timersRef = useRef<{[key: string]: {timeout?: NodeJS.Timeout, interval?: NodeJS.Timeout}}>({});
  const { idleTimer, setIsIdle } = useIdleTimeout({ idleTime: 60, disableIdleTimer: false });

  useEffect(() => {
    const countdownTimer = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(countdownTimer);
          handleResetCart();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownTimer);
      // Clean up all category timers
      Object.values(timersRef.current).forEach(({timeout, interval}) => {
        if (timeout) clearTimeout(timeout);
        if (interval) clearInterval(interval);
      });
      timersRef.current = {};
    };
  }, []);

  const handleResetCart = () => {
    setIdleState(false)
    setIsIdle(false);
    idleTimer.reset();
    resetOrderCart();
  }

  function addTimeoutsByCategory(arr: any[], categories: any[]) {
    logger.log("addTimeoutsByCategory 함수 시작");
    
    // 기본 검증
    if (!Array.isArray(arr) || arr.length === 0) {
      logger.error("카테고리 배열이 비어있거나 유효하지 않습니다", arr);
      return;
    }
    
    if (!Array.isArray(categories) || categories.length === 0) {
      logger.error("타이머 정보가 없습니다", categories);
      return;
    }
    
    logger.log("카테고리 배열:", arr);
    logger.log("타이머 정보:", categories);
    
    // 기존 타이머 정리
    Object.values(timersRef.current).forEach(({timeout, interval}) => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
    });
    timersRef.current = {};
    
    // 카테고리에 타이머 설정
    let updatedCategories = [...arr];
    
    for (const timerInfo of categories) {
      if (!timerInfo.categoryId || !timerInfo.orderIntervalMinutes) {
        logger.log("유효하지 않은 타이머 정보:", timerInfo);
        continue;
      }
      
      const categoryIndex = updatedCategories.findIndex(cat => cat.id === timerInfo.categoryId);
      
      if (categoryIndex === -1) {
        logger.log(`ID가 ${timerInfo.categoryId}인 카테고리를 찾을 수 없습니다.`);
        continue;
      }
      
      const category = updatedCategories[categoryIndex];
      logger.log(`카테고리 ${timerInfo.categoryId}에 타이머 설정 시작`);
      
      const timeoutDuration = timerInfo.orderIntervalMinutes * 60 * 1000;
      const timeoutEnd = Date.now() + timeoutDuration;
      
      logger.log(`타이머 시간: ${timerInfo.orderIntervalMinutes}분, 종료 시간: ${new Date(timeoutEnd).toLocaleTimeString()}`);
      
      // 타임아웃 설정
      const timeoutId = setTimeout(() => {
        logger.log(`카테고리 ${category.id} 타이머 완료`);
        
        const currentCategories = [...updatedCategories];
        const index = currentCategories.findIndex(cat => cat.id === category.id);
        
        if (index !== -1) {
          logger.log(`타이머 완료 처리 - 카테고리 ID: ${category.id}`);
          
          // 타이머 정리
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            if (timeout) clearTimeout(timeout);
            if (interval) clearInterval(interval);
            delete timersRef.current[category.id];
          }
          
          // 카테고리 상태 업데이트
          currentCategories[index] = {
            ...currentCategories[index],
            timeoutEnd: undefined,
            remainingMinutes: undefined,
            timerCategoryId: undefined
          };
          
          logger.log("타이머 제거 후 카테고리:", currentCategories[index]);
          setCategories([...currentCategories]);
          deleteTimerInfo(category.id);
        }
      }, timeoutDuration);
      
      // 인터벌 설정
      const intervalId = setInterval(() => {
        const currentCategories = [...updatedCategories];
        const index = currentCategories.findIndex(cat => cat.id === category.id);
        
        if (index === -1) {
          logger.log(`카테고리 ${category.id}가 더 이상 존재하지 않습니다. 인터벌 정리`);
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            if (timeout) clearTimeout(timeout);
            if (interval) clearInterval(interval);
            delete timersRef.current[category.id];
          }
          return;
        }
        
        const now = Date.now();
        const remainingTime = timeoutEnd - now;
        
        if (remainingTime <= 0) {
          logger.log(`카테고리 ${category.id} 타이머 종료 - 시간 만료`);
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            if (timeout) clearTimeout(timeout);
            if (interval) clearInterval(interval);
            delete timersRef.current[category.id];
          }
          
          currentCategories[index] = {
            ...currentCategories[index],
            timeoutEnd: undefined,
            remainingMinutes: undefined,
            timerCategoryId: undefined
          };
          
          setCategories([...currentCategories]);
          return;
        }
        
        const minutes = Math.ceil(remainingTime / 60000);
        logger.log(`카테고리 ${category.id} 남은 시간: ${minutes}분 (${remainingTime}ms)`);
        currentCategories[index].remainingMinutes = minutes;
        setCategories([...currentCategories]);
      }, 1000);
      
      // 타이머 저장
      timersRef.current[category.id] = {
        timeout: timeoutId,
        interval: intervalId
      };
      
      // 카테고리 업데이트
      updatedCategories[categoryIndex] = {
        ...category,
        timeoutEnd,
        timerCategoryId: category.id,
        remainingMinutes: timerInfo.orderIntervalMinutes
      };

      setTimerInfo(category.id, {
        timeoutEnd,
        timerCategoryId: category.id,
        remainingMinutes: timerInfo.orderIntervalMinutes,
        orderIntervalMinutes: timerInfo.orderIntervalMinutes
      });
    }
    
    setCategories(updatedCategories);
  }
  
  const handleOrderCart = async () => {
    if(processing) return
    setProcessing(true)
    const totalAmount = totalPrice;
    const totalQuantity = totalCount;
    const tableId = tableInfo?.id || 0;
    const tableName = tableInfo?.tableName || '';
    const orderItems = cart;
    // setTotalCnt(totalQuantity)
    // setTotalAmount(totalAmount)
    if(orderItems.length === 0) {
      // setOpenCart(false)
      setSnackMode(5)
      setShowSnackbar(true)
      setOpenCart(false)
      return;
    }

    
    // logger.log('order',orderDetails)
    await createOrder(tableId, totalAmount, totalQuantity, orderItems, storeId, tableName).then( async res => {

      if(res.status === 200 || res.status === 201) {
        logger.log('createOrder',res.data.isFirstOrder)

        // setIsFirst(res.data.isFirstOrder)
      //   setOrderNo(res.data.dailyOrderNo))

      //   // dispatch(setFirstOrder(res.data.isFirstOrder))
        const orderNo = res.data.dailyOrderNo
        setCompleteList({orderItems, orderNo, tableId, totalAmount, totalQuantity})
        if(userInfo?.isAyce)
          addTimeoutsByCategory(categories, categoryInCart);
        showModal();
        logger.log(res.data.isVeronaOrder)
        if(res.data.isVeronaOrder) {
          if(!isBrowser) {
            if(window.ReactNativeWebView !== undefined)
              window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'veronaOrder', 'data': res.data}));
            else if(window.Android !== undefined)
              window.Android.postMessage(JSON.stringify({'type': 'veronaOrder', 'data': res.data}));
          }
        }
        setProcessing(false)
      } else {
        setIdleState(false);
        setProcessing(false)
      }
    })
  }

  const showModal = () => {
    resetOrderCart();
    setOpenComplete(true);
    setOpenCart(false)
    setIdleState(false)
    logger.log('showModal')
  }

  const handleClose = () => {
    setIdleState(false)
  }

  return (
    <Dialog  open={idleState} onClose={setIdleState} PaperProps={{ sx :{justifyContent: 'center', alignItems: 'center', width: '739px', height: '450px', overflow: 'hidden', borderRadius: '1.5rem' } }}>
      <Box sx={{position: 'absolute', top: '1.5rem', left : '3.0rem'}}>
          <img style={{width: '4.0rem', height: '4.0rem'}}
            src={process.env.PUBLIC_URL+'/assets/images/ellipse.svg'}
            alt="ellipse"
          />
          <Typography sx={{fontSize: '1.8rem', fontWeight: '600', textAlign: 'center', color: '#fff', mr: '0rem', mt: '-3.3rem'}}>{time}</Typography>
      </Box>
      <Box sx={{background: '#fff', flexDirection: 'row', width: '100%', height: '6.8rem', borderRadius: '1.5rem 1.5rem'}}>
            <div className="touch-area"  onClick={handleClose}>
              <IconButton aria-label="close" style={{position: 'absolute', right: '3.0rem', top: '0rem', width: '6.6rem', height: '6.6rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}} >
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img style={{position: 'absolute',top: 0, left: 0, width: '100%', height: '100%', }}
                    src={process.env.PUBLIC_URL+'/assets/images/close_bg.svg'}
                    alt="close"
                  />
                  <img style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '2.0rem', height: '2.0rem',}}
                    src={process.env.PUBLIC_URL+'/assets/images/close_w.svg'}
                    alt="close"
                  />
                </div>
              </IconButton>
            </div>
          </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3.0rem 9.0rem', gap: '2.8rem', alignItems: 'center', width: '100%'}}>
        <img src={process.env.PUBLIC_URL+'/assets/images/checklist.svg'} alt="img" style={{ width: "8.0rem", height: "8.0rem", alignSelf: 'center'}}/>
          <Typography component="p" sx={{fontSize: '3.0rem', fontWeight: '600', fontStyle: 'normal', mt: '2.8rem', maxHeight: '18.0rem', width: '50.0rem', textAlign: 'center', 
            lineHeight: '150%', letterSpacing: '-.044', whiteSpace: 'pre-line'}}>{`${'Please check Item in Cart. \n The items in the cart are reset. '}`}</Typography> 
      </Box>
      <DialogActions sx={{gap: '1.5rem'}}>
        <ButtonBase sx={{backgroundColor: '#E9E9E9', p: '.5rem 2.0rem', borderRadius: '5.0rem'}} onClick={handleResetCart}>
          <Typography sx={{fontSize: '2.2rem', fontWeight: '600'}}>Start New Order</Typography>
        </ButtonBase>
        <ButtonBase sx={{backgroundColor: '#DA291C', p: '.5rem 2.0rem', borderRadius: '5.0rem'}} onClick={handleOrderCart}>
          <Typography sx={{fontSize: '2.2rem', fontWeight: '600', color: '#fff'}}>Order Now</Typography>
        </ButtonBase>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmPopup
